// Imports
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { navigate } from 'vike/client/router';

import { authLinkClass, authPrimaryButtonClass } from '@/components/Auth/AuthShell';
import FormAlert from '@/components/Forms/FormAlert';
import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import OtpInput from '@/components/Forms/OtpInput';
import PasswordInput from '@/components/Forms/PasswordInput';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import { NotFoundError } from '@/renderer/utils/errors';
import { useRequest } from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

const EMAIL_REGEX = /\S+@\S+\.\S+/;

// Types
type SetNewPasswordData = { email: string; password: string; code: string };

// API requests
const newPasswordRequest: RequestSchema<API.ResetConfirmState, SetNewPasswordData> = {
  name: 'newPasswordRequest',
  input: '/api/v1/auth/reset-confirm2',
  method: 'POST',
};

const authLoginRequest: RequestSchema<
  { message: string; session: API.Session; user: API.User },
  { username: string; password: string }
> = {
  name: 'authLoginRequest',
  input: '/api/v1/auth/login',
  method: 'POST',
};

export default function SetPassword({ email = '', code = '' }) {
  // Form hooks
  const form = useForm<WithFormErrors<SetNewPasswordData>>();

  // Request hooks
  const request = useRequest(newPasswordRequest, { manual: true, hideAlert: true });
  const authLogin = useRequest(authLoginRequest, { manual: true, hideAlert: true });
  const [success, setSuccess] = useState(false);

  // Handlers
  const handleFormSubmit = useFormSubmit(
    {
      form: form,
      callback: async (body) => {
        try {
          const result = await request.run(body, { throwOnRun: true });

          if (result?.message === 'API.AUTHENTICATION.RESET.CONFIRMED') {
            setSuccess(true);
            await authLogin.run(
              { username: body.email, password: body.password },
              { throwOnRun: true },
            );
            navigate('/dashboard');
          } else {
            form.setError('code', {
              type: 'server',
              message: 'This code is either invalid or expired. Please request a new one.',
            });
          }
        } catch (error) {
          // An invalid/expired code (or unknown email) comes back as a NotFoundError;
          // request.error surfaces it in the alert below and reddens the code boxes.
          if (!(error instanceof NotFoundError)) {
            throw error;
          }
        }
      },
    },
    [],
  );

  return (
    <>
      <div className="flex flex-col items-center text-center">
        <h1 className="font-extrabold text-2xl text-zinc-900 tracking-tight dark:text-white">
          Set new password
        </h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
          Enter the 6-digit code from your reset email, then choose a new password.
        </p>
      </div>
      <form onSubmit={handleFormSubmit} method="POST" className="mt-7 space-y-5">
        <FormControl
          form={form}
          name="email"
          label="Primary email address"
          defaultValue={email}
          register={{
            required: {
              value: true,
              message: 'Email is required',
            },
            pattern: {
              value: EMAIL_REGEX,
              message: 'Email is not a valid email!',
            },
          }}
        />

        <Controller
          control={form.control}
          name="code"
          defaultValue={code}
          rules={{
            required: { value: true, message: 'Enter the 6-digit code from your reset email' },
            pattern: {
              value: /^\d{6}$/,
              message: 'The code is 6 digits',
            },
          }}
          render={({ field, fieldState }) => (
            <div>
              <label
                htmlFor="code"
                className="mb-2 block cursor-pointer font-medium text-base text-zinc-800 leading-6 sm:text-sm dark:text-white"
              >
                Reset code
              </label>
              <OtpInput
                id="code"
                name={field.name}
                aria-label="Reset code"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={!!fieldState.error || !!request.error}
                autoFocus={!code}
                disabled={request.loading || success}
              />
              <label htmlFor="code" className="mt-1 block text-red-500 text-xs empty:hidden">
                {fieldState.error?.message}
              </label>
            </div>
          )}
        />

        <FormControl
          form={form}
          name="password"
          label="New password"
          wrap={PasswordInput}
          register={{
            required: {
              value: true,
              message: 'Password is required',
            },
            minLength: {
              value: 8,
              message: 'Password must be at least 8 characters long',
            },
          }}
        />

        <FormErrors
          form={form}
          request={request}
          rewrite={(err) => (
            <FormAlert>
              {err.code === 404
                ? 'This code or email is invalid or expired. Please request a new one.'
                : err.toString()}
            </FormAlert>
          )}
        />

        <FormSubmitButton
          disabled={request.loading || success}
          loading={request.loading || success}
          className={authPrimaryButtonClass}
        >
          {success ? 'Success! Signing in...' : 'Update password'}
        </FormSubmitButton>

        <p className="text-center text-sm text-zinc-600 dark:text-zinc-400">
          Wrong place?{' '}
          <a href="/signin" className={`whitespace-nowrap ${authLinkClass}`}>
            Back to sign in →
          </a>
        </p>
      </form>
    </>
  );
}
